// Species body plans. `buildAnatomy` creates the separate display objects for one creature
// (body, limbs/appendages) and returns the face geometry so ProceduralCreature can add eyes,
// mouth and accessories generically. This file holds the dispatcher and the classic form of each
// original species; anatomyForms.ts has the other animal + fantasy forms, anatomySynth.ts the
// synth instrument characters, anatomyBoomer.ts the 808-style drum machine. Shared types/helpers live in parts.ts (see its header for the
// coordinate conventions).
import { Graphics } from "pixi.js";
import { INK, mix } from "../palette";
import type { CreatureGenome } from "./genome";
import { blob, blot, chord, disc, noodle, oval } from "./draw";
import {
  antennae,
  bodyOval,
  drawBackSpikes,
  drawBlush,
  drawEars,
  drawHorns,
  drawMarkings,
  drawMohawk,
  drawTufts,
  birdLegs,
  eyeLayout,
  limb,
  newAnatomyParts,
  pal,
  sides,
  snapClaw,
  type Anatomy,
  type Geo,
} from "./parts";
import * as F from "./anatomyForms";
import * as S from "./anatomySynth";
import { boomer, boomerBass } from "./anatomyBoomer";

export type { Anatomy, Colors, Geo, Limb, LimbRole, EyeStyle, MouthStyle } from "./parts";

type Builder = (gn: CreatureGenome, H: number, ow: number) => Anatomy;

const BUILDERS: Record<CreatureGenome["form"], Builder> = {
  beast: thumper,
  frog: F.frog,
  golem: F.golem,
  crab: snapper,
  scorpion: F.scorpion,
  dragon: F.dragon,
  songbird: chirper,
  owl: F.owl,
  fairy: F.fairy,
  seal: clapper,
  penguin: F.penguin,
  yeti: F.yeti,
  jelly: wobbler,
  octopus: F.octopus,
  ghost: F.ghost,
  jellyfish: glimmer,
  firefly: F.firefly,
  wisp: F.wisp,
  bug: critter,
  snail: F.snail,
  mushroom: F.mushroom,
  woodwump: S.woodwump,
  acidmite: S.acidmite,
  choruspede: S.choruspede,
  operatoad: S.operatoad,
  drawbear: S.drawbear,
  boomer,
  boomer_bass: boomerBass,
};

/** Build all form-specific parts. Colors are applied by calling drawers/drawBody afterwards. */
export function buildAnatomy(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const a = (BUILDERS[gn.form] ?? thumper)(gn, H, ow);
  // Round-bodied forms have no real neck: sit the scarf just below the (closed) smile.
  if (gn.form === "beast" || gn.form === "jelly" || gn.form === "bug") {
    const g = a.geo;
    const y = Math.min(Math.max(g.neckY, g.mouthY + g.mouthW * 0.2 + H * 0.07), g.cy + g.ry * 0.8);
    g.neckY = y;
    g.neckHalfW = gn.form === "jelly" && y > g.cy ? g.rx * 1.02 : chord(g.rx, g.ry, y - g.cy) * 1.02;
  }
  return a;
}

// ---------------------------------------------------------------------------------------------
// Thumper / beast: heavy round body, tiny legs, wide mouth.

function thumper(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.1 * gn.limbLength,
    ry = H * 0.44 * gn.heightScale,
    rx = ry * gn.bodyAspect,
    cy = -legH - ry + H * 0.02;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.3);
  const mouthY = cy + ry * 0.22;
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx,
    headTopY: cy - ry,
    headW: rx * 1.1,
    topY: cy - ry - H * 0.12,
    ...e,
    mouthY,
    mouthW: Math.min(gn.mouthWidth * 2 * rx, chord(rx, ry, mouthY - cy) * 1.5) * 0.8,
    neckY: mouthY + H * 0.15,
    neckHalfW: chord(rx, ry, mouthY + H * 0.15 - cy),
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  const tl = gn.tailLength;

  if (gn.tail !== "none") {
    const { l, g } = limb(back, rx * 0.72, cy + ry * 0.55, "tail", 1, 0, 0);
    limbs.push(l);
    drawers.push((c) => {
      g.clear();
      if (gn.tail === "tuft")
        for (let k = 0; k < 3; k++)
          noodle(
            g,
            [0, 0, H * (0.16 + k * 0.02) * tl, -H * (0.02 + k * 0.07) * tl],
            H * 0.06,
            k === 1 ? pal(c, 0) : c.accent,
            ow,
          );
      else
        noodle(
          g,
          [0, 0, H * 0.18 * tl, -H * 0.04, H * 0.2 * tl, -H * 0.16 * tl, H * 0.12 * tl, -H * 0.15 * tl],
          H * 0.06,
          c.base,
          ow,
          true,
        );
    });
  }
  for (const s of sides) {
    const leg = limb(back, s * rx * 0.42, -legH * 1.1, "leg", s, 0, 0);
    limbs.push(leg.l);
    drawers.push((c) => {
      leg.g.clear();
      oval(leg.g, 0, legH * 0.6, H * 0.1, Math.max(H * 0.075, legH * 0.7), c.accent, ow);
    });
    const arm = limb(back, s * rx * 0.95, cy + ry * 0.05, "arm", s, 0, 0.35);
    limbs.push(arm.l);
    drawers.push((c) => {
      arm.g.clear();
      oval(arm.g, 0, H * 0.1 * gn.span, H * 0.065, H * 0.12 * gn.span, c.base, ow);
    });
  }
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0]) => {
    const g = body;
    g.clear();
    drawEars(g, gn, cy - ry * 0.85, rx * 0.62, H, c, ow);
    drawHorns(g, gn, cy - ry * 0.92, rx * 0.4, H, c, ow);
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    blot(g, 0, cy + ry * 0.42, rx * 0.62, ry * 0.45, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Snapper / crab: eyes on stalks, two claws that snap.

function snapper(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const ry = H * 0.27 * gn.heightScale,
    rx = ry * gn.bodyAspect * 1.15,
    cy = -H * 0.2 - ry;
  const stalk = H * 0.1 * gn.limbLength;
  const e = eyeLayout(gn, H, rx * 0.8, cy - ry - stalk, 0.8);
  const cs = gn.clawSize * (0.8 + 0.25 * gn.span);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.2 * cs,
    headTopY: cy - ry,
    headW: rx * 0.8,
    topY: e.eyeY - e.eyeR - H * 0.04,
    ...e,
    mouthY: cy + ry * 0.3,
    mouthW: gn.mouthWidth * 2 * rx * 0.8,
    neckY: cy + ry * 0.05,
    neckHalfW: rx * 0.98,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();

  antennae(back, limbs, drawers, gn, cy - ry * 0.8, rx * 0.25, H * 0.3, H, ow, false);
  for (const s of sides) {
    const legs = limb(back, s * rx * 0.6, cy + ry * 0.4, "leg", s, 0, 0);
    limbs.push(legs.l);
    drawers.push((c) => {
      const g = legs.g;
      g.clear();
      for (let i = 0; i < gn.legPairs; i++) {
        const bx = -i * rx * 0.22,
          kx = s * (rx * 0.3 + H * 0.06 - i * H * 0.07),
          fx = s * (rx * 0.32 + H * 0.14 - i * H * 0.13);
        noodle(g, [s * bx, 0, kx, -H * 0.06 + i * H * 0.02, fx, -(cy + ry * 0.4)], H * 0.045, c.accent, ow * 0.8, true);
      }
    });
    snapClaw(front, limbs, drawers, gn, s, s * rx * 0.8, cy - ry * 0.15, H, ow, cs);
  }
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0]) => {
    const g = body;
    g.clear();
    const xs = gn.eyeCount === 1 ? [0] : [-e.eyeDx, e.eyeDx];
    for (const x of xs) noodle(g, [x * 0.85, cy - ry * 0.6, x, e.eyeY], H * 0.05, c.base, ow);
    drawHorns(g, gn, cy - ry * 0.9, rx * 0.25, H, c, ow);
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.95, -0.05);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    blot(g, 0, cy + ry * 0.45, rx * 0.7, ry * 0.4, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    drawBlush(g, gn, cy, rx * 0.35, H * 0.1);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Chirper / songbird: tiny round bird, beak (drawn as the mouth), wings, tail tuft, crest.

function chirper(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.2 * gn.limbLength,
    ry = H * 0.36 * gn.heightScale,
    rx = ry * gn.bodyAspect,
    cy = -legH - ry + H * 0.03;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.22, 0.9);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.08,
    headTopY: cy - ry,
    headW: rx * 1.2,
    topY: cy - ry - H * 0.15,
    ...e,
    mouthY: e.eyeY + e.eyeR * 0.9,
    mouthW: H * 0.12 * gn.beakLength,
    neckY: e.eyeY + e.eyeR * 2.2,
    neckHalfW: chord(rx, ry, e.eyeY + e.eyeR * 2.2 - cy),
    floats: false,
    mouthStyle: "beak",
    beakScale: gn.beakLength,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  const tl = gn.tailLength;

  const tail = limb(back, rx * 0.5, cy + ry * 0.55, "tail", 1, 0, 0);
  limbs.push(tail.l);
  drawers.push((c) => {
    const g = tail.g;
    g.clear();
    const n = gn.tail === "fan" ? 5 : gn.tail === "tuft" ? 3 : 1;
    for (let k = 0; k < n; k++) {
      const a = n === 1 ? 0.6 : 0.25 + (k / (n - 1)) * 0.9;
      const L = H * (gn.tail === "fan" ? 0.26 : 0.22) * tl;
      if (gn.tail === "curl")
        noodle(g, [0, 0, L, -H * 0.05, L * 1.1, -L * 0.7, L * 0.7, -L * 0.75], H * 0.06, c.accent, ow, true);
      else noodle(g, [0, 0, Math.cos(a) * L, -Math.sin(a) * L], H * 0.065, k % 2 ? c.base : pal(c, k >> 1), ow);
    }
  });
  birdLegs(back, limbs, drawers, rx * 0.32, cy + ry * 0.8, H, ow);
  for (const s of sides) {
    const wing = limb(front, s * rx * 0.86, cy - ry * 0.05, "wing", s, 0, 0.25);
    limbs.push(wing.l);
    drawers.push((c) => {
      wing.g.clear();
      oval(wing.g, 0, H * 0.13 * gn.span, H * 0.07 * gn.span, H * 0.15 * gn.span, mix(c.base, c.accent, 0.45), ow);
    });
  }
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0]) => {
    const g = body;
    g.clear();
    // Crest feathers (genome horns => crest style), coloured from the palette.
    const crest = gn.horns === "none" ? 1 : gn.horns === "nubs" ? 2 : 3;
    for (let k = 0; k < crest; k++) {
      const a = -Math.PI / 2 + (k - (crest - 1) / 2) * 0.45;
      const L = H * (gn.horns === "curl" ? 0.14 : 0.17);
      noodle(
        g,
        [
          0,
          cy - ry * 0.8,
          Math.cos(a) * L * 0.6,
          cy - ry * 0.8 + Math.sin(a) * L * 0.7,
          Math.cos(a) * L,
          cy - ry * 0.8 + Math.sin(a) * L,
        ],
        H * 0.05,
        pal(c, k),
        ow,
        true,
      );
    }
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    blot(g, 0, cy + ry * 0.4, rx * 0.6, ry * 0.5, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Clapper / seal: head on a plump body, muzzle with whiskers, flippers that clap.

function clapper(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const rx = H * 0.46 * gn.bodyAspect,
    ry = H * 0.3 * gn.heightScale,
    cy = -ry;
  const hr = H * 0.27,
    hy = -ry * 2 - H * 0.09 * gn.limbLength;
  const e = eyeLayout(gn, H, hr * 1.15, hy - H * 0.05, 0.8);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: (cy + hy) / 2,
    halfW: rx,
    headTopY: hy - hr,
    headW: hr * 1.6,
    topY: hy - hr - H * 0.05,
    ...e,
    mouthY: hy + H * 0.16,
    mouthW: gn.mouthWidth * hr * 1.6,
    neckY: hy + hr * 0.8,
    neckHalfW: hr * 1.05,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();

  const rear = new Graphics();
  back.addChild(rear);
  drawers.push((c) => {
    rear.clear();
    const big = (gn.tail === "fan" ? 1.3 : 1) * (0.8 + 0.25 * gn.tailLength);
    for (const s of sides) {
      const x = s * rx * 0.8;
      blob(
        rear,
        [x, -H * 0.1, x + s * H * 0.2 * big, -H * 0.06 * big, x + s * H * 0.22 * big, H * 0.0, x + s * H * 0.05, 0],
        mix(c.base, c.accent, 0.4),
        ow,
        true,
      );
    }
  });
  for (const s of sides) {
    const fl = limb(front, s * rx * 0.72, cy - ry * 0.47, "flipper", s, 0, 0.5);
    limbs.push(fl.l);
    drawers.push((c) => {
      fl.g.clear();
      oval(fl.g, 0, H * 0.13 * gn.span, H * 0.065, H * 0.14 * gn.span, mix(c.base, c.accent, 0.35), ow);
    });
  }
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0]) => {
    const g = body;
    g.clear();
    if (gn.ears === "round")
      for (const s of sides) disc(g, s * hr * 0.8, hy - hr * 0.55, H * 0.045 * gn.span, c.accent, ow);
    drawMohawk(g, gn, 0, hy - hr, H, c, ow);
    drawTufts(g, gn, 0, hy - hr, hr, H, c.base, ow);
    drawBackSpikes(g, gn, 0, hy, hr, hr, H, c, ow, -0.8, -0.2);
    // Fused silhouette: thick outlines first, then fills on top hide the inner seams.
    g.ellipse(0, cy, rx, ry)
      .circle(0, hy, hr)
      .moveTo(-hr * 0.8, hy)
      .lineTo(-rx * 0.6, cy)
      .lineTo(rx * 0.6, cy)
      .lineTo(hr * 0.8, hy)
      .closePath()
      .stroke({ width: ow * 2, color: INK });
    g.ellipse(0, cy, rx, ry).fill({ color: c.base });
    g.circle(0, hy, hr).fill({ color: c.base });
    g.moveTo(-hr * 0.8, hy)
      .lineTo(-rx * 0.6, cy)
      .lineTo(rx * 0.6, cy)
      .lineTo(hr * 0.8, hy)
      .closePath()
      .fill({ color: c.base });
    blot(g, 0, cy + ry * 0.2, rx * 0.6, ry * 0.65, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    oval(g, 0, hy + H * 0.09, H * 0.13, H * 0.075, c.belly, ow * 0.6);
    blot(g, 0, hy + H * 0.05, H * 0.035, H * 0.024, INK);
    for (const s of sides)
      for (let k = 0; k < 3; k++)
        blot(g, s * H * (0.05 + k * 0.025), hy + H * (0.09 + (k % 2) * 0.02), H * 0.008, H * 0.008, INK);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Wobbler / jelly: big jelly blob with a flat-ish bottom. Body outline ripples while holding.

const WOB_N = 40;

function wobbler(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const ry = H * 0.43 * gn.heightScale,
    rx = H * 0.4 * gn.bodyAspect,
    cy = -ry;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.15);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx * 1.08,
    headTopY: cy - ry,
    headW: rx * 1.1,
    topY: cy - ry - H * 0.1,
    ...e,
    mouthY: e.eyeY + e.eyeR + H * 0.09,
    mouthW: gn.mouthWidth * 2 * rx * 0.7,
    neckY: cy + ry * 0.35,
    neckHalfW: rx * 1.02,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();

  antennae(back, limbs, drawers, gn, cy - ry * 0.85, 0, H * 0.25, H, ow, false);
  for (const s of sides) {
    const pod = limb(back, s * rx * 0.92, cy + ry * 0.3, "pod", s, 0, 0.6);
    limbs.push(pod.l);
    drawers.push((c) => {
      pod.g.clear();
      oval(pod.g, 0, H * 0.08 * gn.span, H * 0.075, H * 0.11 * gn.span, c.base, ow);
    });
  }
  const pts = new Array<number>(WOB_N * 2).fill(0); // reused every redraw
  const fz = gn.fuzz >= 0.25 ? gn.fuzz : 0;
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0], ripple: number, phase: number) => {
    const g = body;
    g.clear();
    drawHorns(g, gn, cy - ry * 0.9, rx * 0.35, H, c, ow);
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow);
    for (let i = 0; i < WOB_N; i++) {
      const a = (i / WOB_N) * Math.PI * 2;
      const sa = Math.sin(a),
        ca = Math.cos(a);
      const yy = sa > 0 ? Math.pow(sa, 0.4) : sa;
      const widen = 1 + 0.08 * Math.max(sa, 0);
      // Fuzzy jellies get a gentle fringe on the upper half.
      const fur = sa < 0.2 ? fz * 0.06 * (i % 2 ? 1 : -0.4) : 0;
      const rip = 1 + fur + ripple * 0.07 * Math.sin(a * 6 + phase) + ripple * 0.03 * Math.sin(a * 3 - phase * 0.7);
      pts[i * 2] = ca * rx * widen * rip;
      pts[i * 2 + 1] = Math.min(cy + yy * ry * (sa > 0 ? 1 : rip), 0);
    }
    blob(g, pts, c.base, ow, true);
    blot(g, 0, cy + ry * 0.5, rx * 0.65, ry * 0.32, c.belly, 0.8);
    drawMarkings(g, gn, cy, rx * 0.9, ry * 0.9, H, c, 0.85);
    blot(g, -rx * 0.45, cy - ry * 0.5, rx * 0.16, ry * 0.1, 0xffffff, 0.6);
    blot(g, -rx * 0.62, cy - ry * 0.28, rx * 0.05, ry * 0.05, 0xffffff, 0.6);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Glimmer / jellyfish: floating bell with glowing spots and swaying tendrils.

function glimmer(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const rx = H * 0.3 * gn.bodyAspect,
    ry = H * 0.38 * gn.heightScale,
    by = -H * 0.5; // bell rim y
  const e = eyeLayout(gn, H, rx, by - H * 0.15, 0.85);
  const geo: Geo = {
    cy: by - ry * 0.45,
    rx: rx * 0.9,
    ry: ry * 0.5,
    centerY: by - ry * 0.3,
    halfW: rx,
    headTopY: by - ry,
    headW: rx * 1.2,
    topY: by - ry - H * 0.2,
    ...e,
    mouthY: by - H * 0.04,
    mouthW: gn.mouthWidth * rx * 1.4,
    neckY: by + H * 0.035,
    neckHalfW: rx * 0.98,
    floats: true,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();

  antennae(back, limbs, drawers, gn, by - ry * 0.85, rx * 0.35, H * 0.22, H, ow, true);
  const n = gn.tendrils;
  const L = (-by - H * 0.06) * (0.7 + 0.35 * gn.tailLength);
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
    const td = limb(back, t * rx * 0.7, by - H * 0.02, "tendril", 0, i, 0);
    limbs.push(td.l);
    const w = H * 0.03 * (i % 2 ? 0.8 : 1);
    const wig = (i % 2 ? -1 : 1) * H * 0.04;
    drawers.push((c) => {
      td.g.clear();
      noodle(
        td.g,
        [0, 0, wig, L * 0.25, -wig, L * 0.5, wig * 0.8, L * 0.75, 0, L * (0.9 + 0.1 * Math.abs(t))],
        w,
        i % 2 ? pal(c, i >> 1) : c.base,
        ow * 0.7,
        true,
      );
    });
  }
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0]) => {
    const g = body;
    g.clear();
    if (gn.ears === "fin")
      for (const s of sides)
        blob(
          g,
          [
            s * rx * 0.8,
            by - ry * 0.5,
            s * (rx + H * 0.1 * gn.span),
            by - ry * 0.7,
            s * (rx + H * 0.06 * gn.span),
            by - ry * 0.1,
            s * rx * 0.85,
            by,
          ],
          c.accent,
          ow,
          true,
        );
    drawMohawk(g, gn, 0, by - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, by, rx, ry, H, c, ow, -0.85, -0.15);
    // Bell: top half-ellipse + scalloped rim.
    const steps = 18;
    g.moveTo(-rx, by);
    for (let i = 1; i <= steps; i++) {
      const a = Math.PI + (i / steps) * Math.PI;
      g.lineTo(Math.cos(a) * rx, by + Math.sin(a) * ry);
    }
    const k = 5,
      w = (2 * rx) / k;
    for (let i = 0; i < k; i++) {
      const x0 = rx - i * w,
        x1 = x0 - w;
      g.quadraticCurveTo((x0 + x1) / 2, by + H * 0.09, x1, by);
    }
    g.closePath().fill({ color: c.base }).stroke({ width: ow, color: INK, join: "round" });
    blot(g, 0, by - ry * 0.5, rx * 0.7, ry * 0.38, c.light, 0.45);
    blot(g, -rx * 0.45, by - ry * 0.68, rx * 0.14, ry * 0.08, 0xffffff, 0.7);
    drawMarkings(
      g,
      gn,
      by - ry * 0.45,
      rx * 0.9,
      ry * 0.5,
      H,
      gn.markings.multi ? c : mix(c.light, 0xfff3a8, 0.5),
      0.9,
    );
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------
// Critter / bug: small round bug with antennae and little legs.

function critter(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.1 * gn.limbLength,
    ry = H * 0.34 * gn.heightScale,
    rx = ry * gn.bodyAspect,
    cy = -legH - ry + H * 0.04;
  const e = eyeLayout(gn, H, rx, cy - ry * 0.18);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.1,
    headTopY: cy - ry,
    headW: rx * 1.1,
    topY: cy - ry - H * 0.3,
    ...e,
    mouthY: e.eyeY + e.eyeR + H * 0.08,
    mouthW: gn.mouthWidth * 2 * rx * 0.75,
    neckY: cy + ry * 0.45,
    neckHalfW: chord(rx, ry, ry * 0.45),
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();

  antennae(back, limbs, drawers, gn, cy - ry * 0.8, rx * 0.4, H * 0.3, H, ow, false);
  if (gn.tail === "stinger") {
    const st = limb(back, rx * 0.6, cy + ry * 0.55, "tail", 1, 0, 0);
    limbs.push(st.l);
    const L = H * 0.2 * gn.tailLength;
    drawers.push((c) => {
      st.g.clear();
      blob(st.g, [0, -H * 0.05, L, H * 0.02, 0, H * 0.06], c.accent, ow, false);
    });
  }
  for (const s of sides) {
    const legs = limb(back, s * rx * 0.5, cy + ry * 0.6, "leg", s, 0, 0);
    limbs.push(legs.l);
    drawers.push((c) => {
      const g = legs.g;
      g.clear();
      for (let i = 0; i < gn.legPairs; i++) {
        const sx = s * (-i * rx * 0.18),
          kx = s * (rx * 0.45 + i * H * 0.03) * (0.85 + 0.2 * gn.span),
          fy = -(cy + ry * 0.6);
        noodle(g, [sx, 0, kx, fy * 0.3, kx + s * H * 0.05, fy], H * 0.04, c.accent, ow * 0.8);
      }
    });
  }
  const drawBody = (c: Parameters<Anatomy["drawBody"]>[0]) => {
    const g = body;
    g.clear();
    drawHorns(g, gn, cy - ry * 0.85, rx * 0.55, H, c, ow);
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow);
    bodyOval(g, gn, 0, cy, rx, ry, c.base, ow);
    blot(g, 0, cy + ry * 0.45, rx * 0.6, ry * 0.4, c.belly);
    drawMarkings(g, gn, cy, rx, ry, H, c);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}
